import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { lazy, Suspense, type ReactNode } from "react";
import NotFound from "@/pages/NotFound";
import Login from "@/pages/login/page";
import AuthGuard from "@/components/feature/AuthGuard";
import AdminGuard from "@/components/feature/AdminGuard";
import AdminRouteGuard from "@/components/feature/AdminRouteGuard";
import AppShell from "@/components/feature/AppShell";
import PageSkeleton from "@/components/feature/PageSkeleton";

// Lazy-load từng trang (code splitting) để giảm kích thước bundle tải ban đầu,
// giúp web mở nhanh hơn. Các thư viện nặng như máy quét mã vạch (@zxing),
// biểu đồ (recharts) hay xuất Excel (xlsx) chỉ tải khi vào đúng trang cần chúng.
const ScanPage = lazy(() => import("@/pages/scan/page"));
const OverviewPage = lazy(() => import("@/pages/overview/page"));
const CameraSettingsPage = lazy(() => import("@/pages/camera-settings/page"));
const PackingVideosPage = lazy(() => import("@/pages/videos/page"));
const ReturnVideosPage = lazy(() => import("@/pages/videos/page-returns"));
const PublicVideoPage = lazy(() => import("@/pages/watch/page"));
const InboundPage = lazy(() => import("@/pages/inbound/page"));
const ReturnsPage = lazy(() => import("@/pages/returns/page"));
const ProductsPage = lazy(() => import("@/pages/products/page"));
const CombosPage = lazy(() => import("@/pages/combos/page"));
const StocktakePage = lazy(() => import("@/pages/stocktake/page"));
const StocktakeHistoryPage = lazy(() => import("@/pages/stocktake/history/page"));
const MaterialsPage = lazy(() => import("@/pages/materials/page"));
const MaterialsStocktakePage = lazy(() => import("@/pages/materials/stocktake/page"));
const DashboardPage = lazy(() => import("@/pages/dashboard/page"));
const HandoverPage = lazy(() => import("@/pages/handover/page"));
const HandoverSignPage = lazy(() => import("@/pages/handover/sign"));
const BusinessReportPage = lazy(() => import("@/pages/business/page"));
const StaffPage = lazy(() => import("@/pages/staff/page"));
const StaffReportPage = lazy(() => import("@/pages/staff/report/page"));
const StorageHistoryPage = lazy(
  () => import("@/pages/videos/storage-history/page")
);

// Bọc lazy component trong Suspense. Trong lúc chờ mã nguồn trang mới, hiển thị
// khung xương (skeleton) đúng bố cục để trang không bị nhảy và menu bên trái
// vẫn đứng yên, không phải chờ dữ liệu.
function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

const routes: RouteObject[] = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/xem/:id",
    element: (
      <LazyPage>
        <PublicVideoPage />
      </LazyPage>
    ),
  },
  {
    path: "/xem-hang-hoan/:id",
    element: (
      <LazyPage>
        <PublicVideoPage />
      </LazyPage>
    ),
  },
  // TRANG CÔNG KHAI: bưu tá quét mã QR là mở được, KHÔNG đi qua AuthGuard,
  // không yêu cầu đăng nhập. Việc đọc/ghi biên bản đi qua Edge Function
  // handover-sign (service role) nên không cần mở quyền ghi bảng cho khách.
  {
    path: "/handover/sign/:id",
    element: (
      <LazyPage>
        <HandoverSignPage />
      </LazyPage>
    ),
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/scan" replace />,
      },
      {
        path: "tong-quan",
        element: (
          <LazyPage>
            <OverviewPage />
          </LazyPage>
        ),
      },
      {
        path: "scan",
        element: (
          <LazyPage>
            <ScanPage />
          </LazyPage>
        ),
      },
      {
        path: "cai-dat-camera",
        element: (
          <LazyPage>
            <CameraSettingsPage />
          </LazyPage>
        ),
      },
      {
        path: "video-dong-goi",
        element: (
          <LazyPage>
            <PackingVideosPage />
          </LazyPage>
        ),
      },
      {
        path: "video-hang-hoan",
        element: (
          <LazyPage>
            <ReturnVideosPage />
          </LazyPage>
        ),
      },
      {
        path: "inbound",
        element: (
          <AdminRouteGuard>
            <LazyPage>
              <InboundPage />
            </LazyPage>
          </AdminRouteGuard>
        ),
      },
      {
        path: "nhap-hoan",
        element: (
          <LazyPage>
            <ReturnsPage />
          </LazyPage>
        ),
      },
      {
        path: "products",
        element: (
          <LazyPage>
            <ProductsPage />
          </LazyPage>
        ),
      },
      {
        path: "combos",
        element: (
          <LazyPage>
            <CombosPage />
          </LazyPage>
        ),
      },
      {
        path: "kiem-ke",
        element: (
          <AdminRouteGuard>
            <LazyPage>
              <StocktakePage />
            </LazyPage>
          </AdminRouteGuard>
        ),
      },
      {
        path: "kiem-ke/lich-su",
        element: (
          <AdminRouteGuard>
            <LazyPage>
              <StocktakeHistoryPage />
            </LazyPage>
          </AdminRouteGuard>
        ),
      },
      {
        path: "vat-tu",
        element: (
          <LazyPage>
            <MaterialsPage />
          </LazyPage>
        ),
      },
      {
        path: "vat-tu/kiem-ke",
        element: (
          <AdminRouteGuard>
            <LazyPage>
              <MaterialsStocktakePage />
            </LazyPage>
          </AdminRouteGuard>
        ),
      },
      {
        path: "dashboard",
        element: (
          <LazyPage>
            <DashboardPage />
          </LazyPage>
        ),
      },
      {
        path: "handover",
        element: (
          <AdminRouteGuard>
            <LazyPage>
              <HandoverPage />
            </LazyPage>
          </AdminRouteGuard>
        ),
      },
      {
        path: "kinh-doanh",
        element: (
          <AdminGuard trueAdminOnly>
            <LazyPage>
              <BusinessReportPage />
            </LazyPage>
          </AdminGuard>
        ),
      },
      {
        path: "nhan-vien",
        element: (
          <AdminGuard>
            <LazyPage>
              <StaffPage />
            </LazyPage>
          </AdminGuard>
        ),
      },
      {
        path: "nhan-vien/bao-cao",
        element: (
          <LazyPage>
            <StaffReportPage />
          </LazyPage>
        ),
      },
      {
        path: "luu-tru-video",
        element: (
          <AdminRouteGuard>
            <LazyPage>
              <StorageHistoryPage />
            </LazyPage>
          </AdminRouteGuard>
        ),
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;